import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { formatShortLocation } from '@/utils/location';
import {
  MapPin,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import { useAuth } from '@/App';
import { shareLink } from '@/native/share';
import RouteSidebar from '@/pages/route_planner/RouteSidebar';
import RouteSidebarSheet from '@/pages/route_planner/RouteSidebarSheet';
import { getEntityImageUrl, makeImageFallback } from '@/lib/entityMedia';
import {
  SaveRouteDialog,
  ShareRouteDialog,
  SavedRoutesDialog,
} from '@/pages/route_planner/RouteDialogs';
import 'leaflet/dist/leaflet.css';
import 'leaflet-routing-machine/dist/leaflet-routing-machine.css';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Custom numbered marker
const createNumberedIcon = (number, color = '#3B82F6') => {
  return L.divIcon({
    className: 'custom-numbered-marker',
    html: `
      <div style="
        background: ${color};
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        font-size: 14px;
      ">${number}</div>
    `,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -18],
  });
};

// Selectable marker for entities
const createSelectableIcon = (selected = false) => {
  const color = selected ? '#22C55E' : '#6B7280';
  return L.divIcon({
    className: 'selectable-marker',
    html: `
      <div style="
        background: ${color};
        width: 28px;
        height: 28px;
        border-radius: 50%;
        border: 2px solid white;
        box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        ${selected ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3"><polyline points="20,6 9,17 4,12"/></svg>' : ''}
      </div>
    `,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  });
};

// Routing Control Component
const RoutingControl = ({ waypoints, setRouteInfo }) => {
  const map = useMap();
  const routingControlRef = useRef(null);

  // Defensive teardown: leaflet-routing-machine throws when removing a control
  // whose internal `_line` layer was already detached (happens during rapid
  // re-renders). Wrap in try/catch so a stale teardown can't break the UI.
  const safeRemoveControl = (m, control) => {
    if (!m || !control) return;
    try {
      m.removeControl(control);
    } catch (err) {
      // Swallow — control was already torn down by a prior render. Surface
      // at debug level so we still see it if it ever masks a real problem.
      if (process.env.NODE_ENV === 'development') {
        console.debug('safeRemoveControl: stale teardown ignored:', err?.message);
      }
    }
  };

  useEffect(() => {
    if (!map || waypoints.length < 2) {
      // Clean up existing route
      if (routingControlRef.current) {
        safeRemoveControl(map, routingControlRef.current);
        routingControlRef.current = null;
      }
      setRouteInfo(null);
      return;
    }

    // Remove existing control
    if (routingControlRef.current) {
      safeRemoveControl(map, routingControlRef.current);
      routingControlRef.current = null;
    }

    // Create waypoints for routing
    const routeWaypoints = waypoints.map(wp => L.latLng(wp.latitude, wp.longitude));

    // Import routing machine dynamically
    import('leaflet-routing-machine').then((LRM) => {
      const control = L.Routing.control({
        waypoints: routeWaypoints,
        routeWhileDragging: false,
        addWaypoints: false,
        draggableWaypoints: false,
        fitSelectedRoutes: true,
        showAlternatives: false,
        createMarker: () => null, // We'll use our own markers
        lineOptions: {
          styles: [
            { color: '#3B82F6', opacity: 0.8, weight: 6 },
            { color: '#60A5FA', opacity: 1, weight: 4 }
          ]
        },
        router: L.Routing.osrmv1({
          serviceUrl: 'https://router.project-osrm.org/route/v1'
        })
      });

      control.on('routesfound', (e) => {
        const route = e.routes[0];
        setRouteInfo({
          distance: (route.summary.totalDistance / 1000).toFixed(1), // km
          distanceMiles: (route.summary.totalDistance / 1609.34).toFixed(1), // miles
          duration: Math.round(route.summary.totalTime / 60), // minutes
          instructions: route.instructions
        });
      });

      control.on('routingerror', (e) => {
        console.error('Routing error:', e);
        toast.error('Could not calculate route. Please try different destinations.');
      });

      control.addTo(map);
      routingControlRef.current = control;

      // Hide the default routing container
      const container = document.querySelector('.leaflet-routing-container');
      if (container) {
        container.style.display = 'none';
      }
    });

    return () => {
      if (routingControlRef.current) {
        safeRemoveControl(map, routingControlRef.current);
        routingControlRef.current = null;
      }
    };
  }, [map, waypoints, setRouteInfo]);

  return null;
};

const RoutePlanner = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [entities, setEntities] = useState([]);
  const [selectedStops, setSelectedStops] = useState([]);
  const [routeInfo, setRouteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSidebar, setShowSidebar] = useState(true);

  // Save & Share state
  const [savedRoutes, setSavedRoutes] = useState([]);
  const [currentRoute, setCurrentRoute] = useState(null); // {route_id, share_token, name, ...}
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [savedDrawerOpen, setSavedDrawerOpen] = useState(false);
  const [saveForm, setSaveForm] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);

  const fetchEntities = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/entities?limit=100`);
      // Filter only entities with coordinates
      const withCoords = (response.data.entities || []).filter(e => e.latitude && e.longitude);
      setEntities(withCoords);
    } catch (error) {
      console.error('Error fetching entities:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMyRoutes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/routes/mine`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      setSavedRoutes(response.data.routes || []);
    } catch (err) {
      // Non-fatal — anonymous viewer (no token) or transient network blip.
      // Log at debug only so the dev console isn't noisy on every Route Planner mount.
      if (process.env.NODE_ENV === 'development' && err?.response?.status !== 401) {
        console.debug('fetchMyRoutes failed (non-fatal):', err?.message);
      }
    }
  }, [token]);

  useEffect(() => {
    fetchEntities();
  }, [fetchEntities]);

  useEffect(() => {
    if (user) fetchMyRoutes();
  }, [user, fetchMyRoutes]);

  const addStop = (entity) => {
    if (selectedStops.find(s => s.entity_id === entity.entity_id)) {
      toast.info('This destination is already in your route');
      return;
    }
    if (selectedStops.length >= 10) {
      toast.warning('Maximum 10 stops allowed per route');
      return;
    }
    setSelectedStops([...selectedStops, entity]);
    toast.success(`Added ${entity.name} to route`);
  };

  const removeStop = (entityId) => {
    setSelectedStops(selectedStops.filter(s => s.entity_id !== entityId));
  };

  const moveStop = (index, direction) => {
    const newStops = [...selectedStops];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newStops.length) return;
    [newStops[index], newStops[newIndex]] = [newStops[newIndex], newStops[index]];
    setSelectedStops(newStops);
  };

  const clearRoute = () => {
    setSelectedStops([]);
    setRouteInfo(null);
    setCurrentRoute(null);
    toast.info('Route cleared');
  };

  const openSaveDialog = () => {
    if (selectedStops.length === 0) {
      toast.warning('Add at least one stop before saving');
      return;
    }
    if (!user) {
      toast.error('Please log in to save routes');
      return;
    }
    setSaveForm({
      name: currentRoute?.name || `Route · ${new Date().toLocaleDateString()}`,
      description: currentRoute?.description || '',
    });
    setSaveDialogOpen(true);
  };

  const saveRoute = async () => {
    if (!saveForm.name.trim()) {
      toast.warning('Give your route a name');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: saveForm.name.trim(),
        description: saveForm.description.trim() || null,
        entity_ids: selectedStops.map((s) => s.entity_id),
        distance_km: routeInfo?.distance ? parseFloat(routeInfo.distance) : null,
        duration_min: routeInfo?.duration ?? null,
      };

      let saved;
      if (currentRoute?.route_id) {
        // Update existing
        const res = await axios.put(`${API}/routes/${currentRoute.route_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
        saved = res.data;
        toast.success('Route updated');
      } else {
        const res = await axios.post(`${API}/routes`, payload, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
        saved = res.data;
        toast.success('Route saved');
      }
      setCurrentRoute(saved);
      setSaveDialogOpen(false);
      fetchMyRoutes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save route');
    } finally {
      setSaving(false);
    }
  };

  const loadRoute = (route) => {
    setSelectedStops(route.stops || []);
    setCurrentRoute(route);
    setSavedDrawerOpen(false);
    toast.success(`Loaded "${route.name}"`);
  };

  const deleteSavedRoute = async (routeId) => {
    if (!window.confirm('Delete this saved route? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/routes/${routeId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      toast.success('Route deleted');
      if (currentRoute?.route_id === routeId) setCurrentRoute(null);
      fetchMyRoutes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete route');
    }
  };

  const buildShareUrl = (token_) =>
    `${window.location.origin}/r/${token_}`;

  const openShareDialog = async () => {
    if (selectedStops.length === 0) {
      toast.warning('Add stops to your route first');
      return;
    }
    // If route isn't saved yet, save it silently first so we have a share token.
    if (!currentRoute?.share_token) {
      if (!user) {
        toast.error('Please log in to share routes');
        return;
      }
      try {
        const res = await axios.post(`${API}/routes`, {
          name: `Route · ${new Date().toLocaleDateString()}`,
          entity_ids: selectedStops.map((s) => s.entity_id),
          distance_km: routeInfo?.distance ? parseFloat(routeInfo.distance) : null,
          duration_min: routeInfo?.duration ?? null,
        }, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
        setCurrentRoute(res.data);
        fetchMyRoutes();
      } catch (err) {
        toast.error('Could not prepare share link');
        return;
      }
    }
    setShareDialogOpen(true);
  };

  const copyShareUrl = async () => {
    if (!currentRoute?.share_token) return;
    try {
      await navigator.clipboard.writeText(buildShareUrl(currentRoute.share_token));
      toast.success('Share link copied');
    } catch (e) {
      toast.error('Could not copy link');
    }
  };

  // Tries native share sheet on iOS/Android, Web Share API on mobile
  // browsers, falls back to clipboard everywhere else.
  const shareRouteLink = async () => {
    if (!currentRoute?.share_token) return;
    const url = buildShareUrl(currentRoute.share_token);
    const title = currentRoute.name || 'A route on TheUnityBlaze';
    const result = await shareLink({
      title,
      text: 'Check out this route I planned on TheUnityBlaze.',
      url,
      dialogTitle: 'Share route',
    });
    if (result.method === 'clipboard') {
      toast.success('Share link copied to clipboard');
    } else if (result.method === 'native' || result.method === 'web') {
      // Native share sheet shows its own success UI; no toast needed.
    } else if (!result.ok && result.method !== 'cancelled') {
      toast.error('Could not share — please try Copy instead.');
    }
  };

  const regenerateShareToken = async () => {
    if (!currentRoute?.route_id) return;
    try {
      const res = await axios.post(
        `${API}/routes/${currentRoute.route_id}/regenerate-share`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      setCurrentRoute({ ...currentRoute, share_token: res.data.share_token });
      toast.success('New share link generated. The old one is now invalid.');
    } catch (err) {
      toast.error('Failed to regenerate link');
    }
  };

  const filteredEntities = entities.filter(e => {
    if (!searchQuery) return true;
    return e.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
           e.location?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const defaultCenter = [39.8283, -98.5795];

  return (
    <div className="min-h-screen bg-background" data-testid="route-planner-page">
      <Navigation />
      
      <main className="pt-16 h-[calc(100vh-64px)] flex flex-col md:flex-row">
        {/* Sidebar — desktop persistent left panel */}
        <RouteSidebar
          user={user}
          showSidebar={showSidebar}
          currentRoute={currentRoute}
          selectedStops={selectedStops}
          routeInfo={routeInfo}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          filteredEntities={filteredEntities}
          savedRoutes={savedRoutes}
          navigate={navigate}
          openSaveDialog={openSaveDialog}
          setSavedDrawerOpen={setSavedDrawerOpen}
          clearRoute={clearRoute}
          moveStop={moveStop}
          removeStop={removeStop}
          addStop={addStop}
          openShareDialog={openShareDialog}
        />

        {/* Sidebar — mobile bottom sheet triggered by a floating pill */}
        <RouteSidebarSheet
          user={user}
          currentRoute={currentRoute}
          selectedStops={selectedStops}
          routeInfo={routeInfo}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          filteredEntities={filteredEntities}
          savedRoutes={savedRoutes}
          navigate={navigate}
          openSaveDialog={openSaveDialog}
          setSavedDrawerOpen={setSavedDrawerOpen}
          clearRoute={clearRoute}
          moveStop={moveStop}
          removeStop={removeStop}
          addStop={addStop}
          openShareDialog={openShareDialog}
        />

        {/* Toggle Sidebar Button (desktop only — on mobile sidebar is full-width) */}
        <button
          onClick={() => setShowSidebar(!showSidebar)}
          className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-[1000] bg-card border border-border rounded-r-lg p-2 shadow-lg"
          style={{ left: showSidebar ? '384px' : '0' }}
          data-testid="toggle-sidebar-btn"
        >
          {showSidebar ? <ChevronUp className="h-4 w-4 rotate-90" /> : <ChevronDown className="h-4 w-4 rotate-90" />}
        </button>

        {/* Map */}
        <div className="flex-1 relative overflow-hidden">
          {loading ? (
            <div className="w-full h-full flex items-center justify-center bg-muted/30">
              <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <MapContainer
              center={defaultCenter}
              zoom={4}
              style={{ height: '100%', width: '100%' }}
              className="z-0"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              
              {/* Routing Control */}
              <RoutingControl waypoints={selectedStops} setRouteInfo={setRouteInfo} />
              
              {/* Entity Markers */}
              {entities.map((entity) => {
                const isSelected = selectedStops.some(s => s.entity_id === entity.entity_id);
                const stopIndex = selectedStops.findIndex(s => s.entity_id === entity.entity_id);
                
                return (
                  <Marker
                    key={entity.entity_id}
                    position={[entity.latitude, entity.longitude]}
                    icon={isSelected ? createNumberedIcon(stopIndex + 1) : createSelectableIcon(false)}
                    eventHandlers={{
                      click: () => isSelected ? removeStop(entity.entity_id) : addStop(entity)
                    }}
                  >
                    <Popup>
                      <div className="p-1 min-w-[200px]">
                        {(() => {
                          const src = getEntityImageUrl(entity, { width: 400 });
                          if (!src) return null;
                          return (
                            <img
                              src={src}
                              alt={entity.name}
                              className="w-full h-24 object-cover rounded-lg mb-2"
                              onError={makeImageFallback(entity)}
                            />
                          );
                        })()}
                        <h3 className="font-semibold">{entity.name}</h3>
                        <p className="text-sm text-gray-600 flex items-center gap-1 mb-2">
                          <MapPin className="h-3 w-3" />
                          {formatShortLocation(entity)}
                        </p>
                        <Button 
                          size="sm" 
                          className="w-full"
                          onClick={() => isSelected ? removeStop(entity.entity_id) : addStop(entity)}
                        >
                          {isSelected ? 'Remove from Route' : 'Add to Route'}
                        </Button>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          )}
          
          {/* Turn-by-Turn preview overlay removed — the stop list is already
              shown inside the sidebar / mobile bottom sheet under "Your Route",
              and the map-floating panel was covering markers + the Plan FAB. */}
        </div>
      </main>

      {/* Save Route Dialog */}
      <SaveRouteDialog
        open={saveDialogOpen}
        onOpenChange={setSaveDialogOpen}
        saveForm={saveForm}
        setSaveForm={setSaveForm}
        selectedStops={selectedStops}
        routeInfo={routeInfo}
        saving={saving}
        saveRoute={saveRoute}
        currentRoute={currentRoute}
      />

      {/* Share Dialog */}
      <ShareRouteDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        currentRoute={currentRoute}
        buildShareUrl={buildShareUrl}
        copyShareUrl={copyShareUrl}
        shareRouteLink={shareRouteLink}
        regenerateShareToken={regenerateShareToken}
      />

      {/* Saved Routes Drawer */}
      <SavedRoutesDialog
        open={savedDrawerOpen}
        onOpenChange={setSavedDrawerOpen}
        savedRoutes={savedRoutes}
        loadRoute={loadRoute}
        deleteSavedRoute={deleteSavedRoute}
      />
    </div>
  );
};

export default RoutePlanner;
